import type {
  CreateTemplate,
  ReadSigningConfig,
  ReadTemplate,
  ReadTemplateField,
  RenderTemplateRequest,
  TemplateCategory,
  TemplateListApiArg,
  TemplateListApiResponse,
} from './generated/templatesV2Service';
import { enhancedApi } from './generated/templatesV2Service';

// ---------------------------------------------------------------------------
// Field CRUD types
// ---------------------------------------------------------------------------

export type CreateTemplateFieldPayload = {
  reference: string;
  field_type: ReadTemplateField['field_type'];
  source: ReadTemplateField['source'];
  display_name?: string | null;
  context_key?: string | null;
  default_value?: string | null;
  required?: boolean;
  pdf_page_number?: number | null;
  pdf_position?: ReadTemplateField['pdf_position'] | null;
};

export type UpdateTemplateFieldPayload = Partial<CreateTemplateFieldPayload>;

// ---------------------------------------------------------------------------
// Context field types
// ---------------------------------------------------------------------------

export type ContextFieldDefinition = {
  key: string;
  label: string;
  type: string;
  sample: string;
};

export type ContextFieldCategory = {
  key: string;
  label: string;
  fields: ContextFieldDefinition[];
};

export type ContextFieldsResponse = {
  categories: ContextFieldCategory[];
};

// ---------------------------------------------------------------------------
// Bulk operation types
// ---------------------------------------------------------------------------

export type TemplateBulkOperationSkipped = {
  template_id: string;
  reason: string;
};

export type TemplateBulkOperationResponse = {
  processed_count: number;
  skipped: TemplateBulkOperationSkipped[];
};

export type TemplateListMultiApiArg = {
  categories?: NonNullable<TemplateListApiArg['category']>[];
  firmIds?: string[];
  formats?: NonNullable<TemplateListApiArg['templateFormat']>[];
  isDefault?: boolean;
  limit?: number;
  offset?: number;
  ordering?: TemplateListApiArg['ordering'];
  scope?: TemplateListApiArg['scope'];
  search?: string;
  status?: TemplateListApiArg['status'];
};

// ---------------------------------------------------------------------------
// Signing config types
// ---------------------------------------------------------------------------

export type CreateSigningConfigPayload = {
  provider: 'ADOBE_SIGN';
  client_signature_required: boolean;
  attorney_signature_required: boolean;
  default_attorney_id?: string | null;
};

export type UpdateSigningConfigPayload = Partial<CreateSigningConfigPayload>;

// ---------------------------------------------------------------------------
// Blob-returning endpoint arg types (flattened for consumer convenience)
// ---------------------------------------------------------------------------

export type TemplateRenderBlobArg = {
  id: string;
  fields?: RenderTemplateRequest['fields'];
  matterId?: string | null;
};

export type TemplateRenderToPdfBlobArg = {
  id: string;
  fields?: RenderTemplateRequest['fields'];
  matterId?: string | null;
};

export type TemplateNormalizePdfBlobArg = {
  file: Blob;
};

// ---------------------------------------------------------------------------
// Branding packet types
// ---------------------------------------------------------------------------

export type CategoryResolution = {
  category: TemplateCategory;
  template: ReadTemplate | null;
  source_tier: 'firm' | 'global' | 'fallback' | 'none';
};

export type ResolveAllResponse = {
  firm_id: string;
  resolutions: CategoryResolution[];
};

export type BrandingPacketStatusResponse = {
  status: 'running' | 'completed' | 'failed' | 'not_found' | string;
  download_url: string | null;
  compiled_pdf_download_url?: string | null;
  error: string | null;
};

export type RenderBrandedSampleArg = {
  templateId: string;
  firmId: string;
  attorneyId?: string | null;
};

export type GenerateBrandingPacketArg = {
  firmId: string;
  attorneyId?: string | null;
};

// ---------------------------------------------------------------------------
// Enhanced API with field-level + signing config CRUD
// ---------------------------------------------------------------------------

export const templatesV2EnhancedApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    // ---- List (multi-select filters) ----
    templateListMulti: build.query<TemplateListApiResponse, TemplateListMultiApiArg>({
      query: (queryArg) => {
        const params = new URLSearchParams();

        queryArg.categories?.forEach((category) => {
          params.append('category', category);
        });
        queryArg.formats?.forEach((templateFormat) => {
          params.append('template_format', templateFormat);
        });
        queryArg.firmIds?.forEach((firmId) => {
          params.append('firm_id', firmId);
        });

        if (queryArg.isDefault !== undefined) {
          params.set('is_default', String(queryArg.isDefault));
        }
        if (queryArg.limit !== undefined) {
          params.set('limit', String(queryArg.limit));
        }
        if (queryArg.offset !== undefined) {
          params.set('offset', String(queryArg.offset));
        }
        if (queryArg.ordering) {
          params.set('ordering', queryArg.ordering);
        }
        if (queryArg.scope) {
          params.set('scope', queryArg.scope);
        }
        if (queryArg.search) {
          params.set('search', queryArg.search);
        }
        if (queryArg.status) {
          params.set('status', queryArg.status);
        }

        const queryString = params.toString();
        return {
          url: queryString ? `/api/templates/?${queryString}` : '/api/templates/',
        };
      },
      providesTags: ['templates'],
    }),

    // ---- Create (JSON only, no file) ----
    templateCreateUnified: build.mutation<
      ReadTemplate,
      { body: CreateTemplate }
    >({
      query: ({ body }) => ({
        url: '/api/templates/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['templates'],
    }),

    // ---- Create with file upload (multipart/form-data) ----
    templateCreateWithFile: build.mutation<
      ReadTemplate,
      { file: File; scope?: CreateTemplate['scope']; firm_id?: string; attorney_id?: string }
    >({
      query: ({ file, ...fields }) => {
        const formData = new FormData();
        formData.append('file', file);
        Object.entries(fields).forEach(([key, value]) => {
          if (value != null) {
            formData.append(key, value);
          }
        });
        return {
          url: '/api/templates/',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['templates'],
    }),

    templateUploadNewVersion: build.mutation<
      ReadTemplate,
      { templateId: string; file: Blob }
    >({
      query: ({ templateId, file }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: `/api/templates/${templateId}/new-version/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['templates'],
    }),

    // ---- Field CRUD ----
    templateFieldCreate: build.mutation<
      ReadTemplateField,
      { templateId: string; body: CreateTemplateFieldPayload }
    >({
      query: ({ templateId, body }) => ({
        url: `/api/templates/${templateId}/fields/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['templates'],
    }),

    templateFieldUpdate: build.mutation<
      ReadTemplateField,
      { templateId: string; fieldId: string; body: UpdateTemplateFieldPayload }
    >({
      query: ({ templateId, fieldId, body }) => ({
        url: `/api/templates/${templateId}/fields/${fieldId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['templates'],
    }),

    templateFieldDelete: build.mutation<
      void,
      { templateId: string; fieldId: string }
    >({
      query: ({ templateId, fieldId }) => ({
        url: `/api/templates/${templateId}/fields/${fieldId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['templates'],
    }),

    // ---- Signing Config CRUD ----
    templateSigningConfigCreate: build.mutation<
      ReadSigningConfig,
      { templateId: string; body: CreateSigningConfigPayload }
    >({
      query: ({ templateId, body }) => ({
        url: `/api/templates/${templateId}/signing-config/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['templates'],
    }),

    templateSigningConfigUpdate: build.mutation<
      ReadSigningConfig,
      { templateId: string; body: UpdateSigningConfigPayload }
    >({
      query: ({ templateId, body }) => ({
        url: `/api/templates/${templateId}/signing-config/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['templates'],
    }),

    templateSigningConfigDelete: build.mutation<
      void,
      { templateId: string }
    >({
      query: ({ templateId }) => ({
        url: `/api/templates/${templateId}/signing-config/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['templates'],
    }),

    // ---- Restore version ----
    templateRestoreVersion: build.mutation<
      ReadTemplate,
      { templateId: string; version: number }
    >({
      query: ({ templateId, version }) => ({
        url: `/api/templates/${templateId}/restore-version/?version=${version}`,
        method: 'POST',
      }),
      invalidatesTags: ['templates'],
    }),

    // ---- Bulk operations ----
    templateBulkArchive: build.mutation<
      TemplateBulkOperationResponse,
      { ids: string[] }
    >({
      query: ({ ids }) => ({
        url: `/api/templates/bulk-archive/`,
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: ['templates'],
    }),

    // ---- Context fields ----
    templateContextFields: build.query<ContextFieldsResponse, void>({
      query: () => '/api/templates/context-fields/',
    }),

    // ---- Blob-returning endpoints ----
    // RTK Query codegen generates `unknown` for binary responses from OpenAPI
    // schemas. These overrides add proper `responseHandler` to return Blob
    // objects, and flatten the arg types for consumer convenience.

    templateRender: build.mutation<Blob, TemplateRenderBlobArg>({
      query: (queryArg) => ({
        url: `/api/templates/${queryArg.id}/render/`,
        method: 'POST',
        body: { fields: queryArg.fields, matter_id: queryArg.matterId },
        cache: 'no-store' as const,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    templateRenderToPdf: build.mutation<Blob, TemplateRenderToPdfBlobArg>({
      query: (queryArg) => ({
        url: `/api/templates/${queryArg.id}/render-to-pdf/`,
        method: 'POST',
        body: { fields: queryArg.fields, matter_id: queryArg.matterId },
        cache: 'no-store' as const,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    templateNormalizePdf: build.mutation<Blob, TemplateNormalizePdfBlobArg>({
      query: (queryArg) => {
        const formData = new FormData();
        formData.append('file', queryArg.file);
        return {
          url: `/api/templates/normalize-pdf/`,
          method: 'POST',
          body: formData,
          cache: 'no-store' as const,
          responseHandler: (response: Response) => response.blob(),
        };
      },
    }),

    // ---- Branding / resolve-all ----
    templateResolveAll: build.query<ResolveAllResponse, { firmId: string }>({
      query: ({ firmId }) => ({
        url: `/api/templates/resolve-all/`,
        params: { firm_id: firmId },
      }),
      providesTags: ['templates'],
    }),

    // ---- Render branded sample (returns PDF blob) ----
    templateRenderBrandedSample: build.mutation<Blob, RenderBrandedSampleArg>({
      query: ({ templateId, firmId, attorneyId }) => ({
        url: `/api/templates/${templateId}/render-branded-sample/`,
        method: 'POST',
        body: { firm_id: firmId, attorney_id: attorneyId ?? null },
        cache: 'no-store' as const,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // ---- Generate branding packet (starts Temporal workflow) ----
    templateGenerateBrandingPacket: build.mutation<
      { workflow_id: string; status: string },
      GenerateBrandingPacketArg
    >({
      query: ({ firmId, attorneyId }) => ({
        url: `/api/templates/generate-branding-packet/`,
        method: 'POST',
        body: { firm_id: firmId, attorney_id: attorneyId ?? null },
      }),
      invalidatesTags: ['templates'],
    }),

    // ---- Poll branding packet status ----
    templateBrandingPacketStatus: build.query<
      BrandingPacketStatusResponse,
      { firmId: string }
    >({
      query: ({ firmId }) => ({
        url: `/api/templates/branding-packet-status/`,
        params: { firm_id: firmId },
      }),
      providesTags: ['templates'],
    }),
  }),
  overrideExisting: true,
});

export const {
  useTemplateListMultiQuery,
  useTemplateCreateUnifiedMutation,
  useTemplateCreateWithFileMutation,
  useTemplateUploadNewVersionMutation,
  useTemplateFieldCreateMutation,
  useTemplateFieldUpdateMutation,
  useTemplateFieldDeleteMutation,
  useTemplateSigningConfigCreateMutation,
  useTemplateSigningConfigUpdateMutation,
  useTemplateSigningConfigDeleteMutation,
  useTemplateRestoreVersionMutation,
  useTemplateBulkArchiveMutation,
  useTemplateContextFieldsQuery,
  useTemplateRenderMutation,
  useTemplateRenderToPdfMutation,
  useTemplateNormalizePdfMutation,
  useTemplateResolveAllQuery,
  useTemplateRenderBrandedSampleMutation,
  useTemplateGenerateBrandingPacketMutation,
  useTemplateBrandingPacketStatusQuery,
} = templatesV2EnhancedApi;
